import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { useEffect } from 'react';
import '@mantine/core/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import { AuthProvider } from '../AuthProvider';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            retry: 1,
        },
        mutations: {
            retry: 0,
        },
    },
});

export interface GlobalProviderProps {
    children?: React.ReactNode;
}

const isInsideAppShell = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) {
        return false;
    }
    return Boolean(target.closest('#root'));
};

// Only text-like inputs support selectionStart/setSelectionRange; calling it on
// email/number/date/etc. throws InvalidStateError. textarea always supports it.
const SELECTABLE_INPUT_TYPES = new Set(['text', 'search', 'tel', 'url', 'password']);

const keepCaretAtEnd = (element: Element | null): void => {
    if (
        element instanceof HTMLTextAreaElement ||
        (element instanceof HTMLInputElement && SELECTABLE_INPUT_TYPES.has(element.type))
    ) {
        const end = element.value.length;
        if (element.selectionStart !== end || element.selectionEnd !== end) {
            element.setSelectionRange(end, end);
        }
    }
};

const useNoSelectInputGuards = (): void => {
    useEffect(() => {
        const handleContextMenu = (event: Event) => {
            if (isInsideAppShell(event.target)) {
                event.preventDefault();
            }
        };

        const handleSelectStart = (event: Event) => {
            if (isInsideAppShell(event.target)) {
                event.preventDefault();
            }
        };

        const handleSelectionChange = () => {
            keepCaretAtEnd(document.activeElement);
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed) {
                return;
            }
            if (
                isInsideAppShell(selection.anchorNode) ||
                isInsideAppShell(selection.focusNode)
            ) {
                selection.removeAllRanges();
            }
        };

        document.addEventListener('contextmenu', handleContextMenu, true);
        document.addEventListener('selectstart', handleSelectStart, true);
        document.addEventListener('selectionchange', handleSelectionChange, true);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu, true);
            document.removeEventListener('selectstart', handleSelectStart, true);
            document.removeEventListener('selectionchange', handleSelectionChange, true);
        };
    }, []);
};

const InputSelectionGuards: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    useNoSelectInputGuards();
    return <>{children}</>;
};

// Summary: Composes app-wide providers once and preserves provider order assumptions.
// Details: docs/function-library/web/global-provider.md#app-wide-provider-composition
export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => (
    <MantineProvider defaultColorScheme="light">
        <Notifications position="top-center" autoClose={4000} limit={3} zIndex={2000} />
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <InputSelectionGuards>
                    {children}
                </InputSelectionGuards>
            </AuthProvider>
        </QueryClientProvider>
    </MantineProvider>
);
